import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { unlockAudio, getMuted, setMuted } from "@/lib/sound";
import { CheckoutOverlay } from "@/components/CheckoutOverlay";
import VideoCall from "@/components/VideoCall";
import RevealAnimation from "@/components/RevealAnimation";
import { Sparkles, Video as VideoIcon, Circle, Loader2, PartyPopper, Clock, LogOut, Lock, CalendarClock, ExternalLink, Copy, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In-app browsers (Messenger, Facebook, Instagram, TikTok, etc.) block camera/mic on Android.
function detectInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /FBAN|FBAV|FB_IAB|Messenger|Instagram|Line\/|Twitter|MicroMessenger|TikTok|Snapchat|Pinterest|WhatsApp|GSA/i.test(ua);
}
function detectPlatform() {
  const ua = (typeof navigator !== "undefined" && (navigator.userAgent || "")) || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "other";
}
// Try to hand off from an in-app webview to the real system browser.
function openInSystemBrowser() {
  const url = window.location.href;
  const platform = detectPlatform();
  if (platform === "android") {
    // Android intent:// reliably launches Chrome from most in-app webviews.
    const noScheme = url.replace(/^https?:\/\//, "");
    window.location.href = `intent://${noScheme}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
    return true;
  }
  if (platform === "ios") {
    // No public API forces Safari from an iOS in-app webview; best effort + let the banner guide.
    window.open(url, "_blank");
    return false;
  }
  window.open(url, "_blank");
  return true;
}

function fmt(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export default function RevealRoom() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [joined, setJoined] = useState(false);
  const [call, setCall] = useState(null);
  const [showAnim, setShowAnim] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [left, setLeft] = useState(null); // null | "left" | "ended"
  const [roomFull, setRoomFull] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [recSecs, setRecSecs] = useState(0); // >0 while recording
  const revealedRef = useRef(false);
  const offsetRef = useRef(0);
  const socketRef = useRef(null);
  const inApp = detectInAppBrowser();
  const platform = detectPlatform();
  const isTouch = typeof window !== "undefined" && (("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0);
  const [soundHint, setSoundHint] = useState(false);
  const [muted, setMutedState] = useState(getMuted());
  const toggleMute = () => { const v = !muted; setMuted(v); setMutedState(v); unlockAudio(); };
  // On Android in-app webviews, auto-hand off to Chrome so guests don't have to copy/paste.
  useEffect(() => {
    if (inApp && platform === "android") {
      const t = setTimeout(() => { try { openInSystemBrowser(); } catch (e) { console.debug("in-app browser handoff failed", e); } }, 400);
      return () => clearTimeout(t);
    }
  }, [inApp, platform]);
  const copyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => toast.success("Link copied — paste it into Chrome or Safari")).catch(() => toast.error("Couldn't copy — long-press the address bar to copy the link"));
    } else {
      toast.message("Copy this link into your browser", { description: url });
    }
  };
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const callRef = useRef(null);

  const handleParticipants = useCallback((list) => setParticipants(list), []);
  const handleCall = useCallback((c) => { callRef.current = c; }, []);

  const loadEvent = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/events/${code}`);
      if (data.server_now) offsetRef.current = Date.now() - new Date(data.server_now).getTime();
      setEvent(data);
      return data;
    } catch {
      toast.error("Reveal not found");
      return null;
    }
  }, [code]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  // global 1s tick
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const effNow = now - offsetRef.current;
  const opensAt = event?.opens_at ? new Date(event.opens_at).getTime() : null;
  const closesAt = event?.closes_at ? new Date(event.closes_at).getTime() : null;
  const status = !event ? "loading" : (opensAt && effNow < opensAt ? "before" : (closesAt && effNow > closesAt ? "ended" : "open"));

  // ---- reaction recording (20s auto) ----
  const stopStream = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const uploadReaction = useCallback(async (blob, ext = "webm") => {
    try {
      const fd = new FormData();
      fd.append("display_name", name || "Guest");
      fd.append("consent", "true");
      fd.append("file", blob, `reaction.${ext}`);
      await api.post(`/public/events/${code}/reactions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Your reaction was added to the highlight reel!");
    } catch {
      toast.error("Could not save your reaction");
    }
  }, [name, code]);

  // Prefer recording Daily's EXISTING camera stream (mobile can't open the camera twice).
  const getReactionStream = useCallback(async () => {
    const c = callRef.current;
    if (c) {
      try {
        const local = c.participants().local;
        const vt = local?.tracks?.video?.persistentTrack;
        const at = local?.tracks?.audio?.persistentTrack;
        const tracks = [vt, at].filter(Boolean);
        if (tracks.length) return { stream: new MediaStream(tracks), owned: false };
      } catch (e) { console.debug("could not reuse Daily stream, opening own", e); }
    }
    // Fallback (desktop): open our own stream
    const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
    return { stream: s, owned: true };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const { stream, owned } = await getReactionStream();
      if (owned) streamRef.current = stream; // only stop streams we created
      const chunks = [];
      // Pick a container the device supports (iOS Safari can't do webm -> mp4).
      const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
      const mime = (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported)
        ? candidates.find((t) => MediaRecorder.isTypeSupported(t)) : null;
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        if (streamRef.current) stopStream();
        setRecSecs(0);
        const type = mr.mimeType || mime || "video/webm";
        const ext = type.includes("mp4") ? "mp4" : "webm";
        uploadReaction(new Blob(chunks, { type }), ext);
      };
      mr.start();
      setRecSecs(20);
      setTimeout(() => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); }, 20000);
    } catch {
      toast.error("Couldn't record your reaction this time");
      setRecSecs(0);
    }
  }, [getReactionStream, uploadReaction]);

  const triggerReveal = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setShowAnim(true);
    startRecording();
  }, [startRecording]);

  // recording countdown display
  useEffect(() => {
    if (recSecs <= 0) return;
    const t = setTimeout(() => setRecSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(t);
  }, [recSecs]);

  // ---- socket.io for reveal sync (instant path) ----
  useEffect(() => {
    if (!joined) return;
    const s = io(BACKEND_URL, { path: "/api/socket.io", transports: ["polling", "websocket"] });
    socketRef.current = s;
    s.on("connect", () => s.emit("join_room", { room_id: code }));
    s.on("reveal_started", (payload) => {
      if (payload?.reveal_value) setEvent((e) => (e ? { ...e, revealed: true, reveal_value: payload.reveal_value } : e));
      triggerReveal();
    });
    return () => { s.disconnect(); socketRef.current = null; };
  }, [joined, code, triggerReveal]);

  // ---- polling fallback for reveal sync ----
  // Guarantees the reveal reaches every guest even when the Socket.IO broadcast
  // can't cross server instances (e.g. a multi-worker / multi-replica production
  // deploy without a shared message queue). Polls the public event's `revealed`
  // flag; triggerReveal is idempotent (guarded by revealedRef).
  useEffect(() => {
    if (!joined || left) return;
    const iv = setInterval(async () => {
      if (revealedRef.current) return;
      try {
        const { data } = await api.get(`/public/events/${code}`);
        if (data?.revealed) { setEvent(data); triggerReveal(); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [joined, left, code, triggerReveal]);

  // auto-close when the 15-min session ends
  useEffect(() => {
    if (joined && !left && closesAt && effNow > closesAt) {
      setLeft("ended");
      stopStream();
    }
  }, [joined, left, closesAt, effNow]);

  // On touch devices, mobile browsers keep the call's remote audio muted until the
  // user taps inside the video. Show a tap-to-enable-sound hint after joining.
  useEffect(() => {
    if (joined && isTouch) {
      setSoundHint(true);
      const t = setTimeout(() => setSoundHint(false), 15000);
      return () => clearTimeout(t);
    }
  }, [joined, isTouch]);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim() || !consent) return;
    unlockAudio();
    try {
      const { data } = await api.post(`/public/events/${code}/join`, { display_name: name.trim() });
      setCall(data);
      setJoined(true);
      const fresh = await loadEvent();
      if (fresh?.revealed) { revealedRef.current = true; } // joined after reveal: skip animation/record
    } catch (err) {
      const code2 = err.response?.status;
      if (code2 === 409) { setRoomFull(true); return; }
      toast.error(err.response?.data?.detail || (code2 === 423 ? "The room isn't open yet" : "Could not join the room"));
    }
  };

  const doReveal = async () => {
    if (!event) return;
    unlockAudio();
    setTriggering(true);
    try {
      const { data } = await api.post(`/events/${event.id}/reveal`);
      setEvent((e) => (e ? { ...e, revealed: true, reveal_value: data.reveal_value, reveal_label: data.reveal_label } : e));
      triggerReveal(); // socket echo also fires; guarded by ref
    } catch {
      toast.error("Could not trigger reveal");
    } finally {
      setTriggering(false);
    }
  };

  const leaveRoom = () => {
    stopStream();
    if (socketRef.current) socketRef.current.disconnect();
    setLeft("left");
  };

  const extendPollRef = useRef(null);
  const pollExtend = useCallback((sessionId) => {
    if (!sessionId) return;
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await loadEvent(); // refresh closes_at -> timer extends, no re-join
          toast.success("Room extended — 5 more minutes added!");
          return;
        }
      } catch { /* keep polling */ }
      if (n < 90) extendPollRef.current = setTimeout(tick, 2000);
    };
    extendPollRef.current = setTimeout(tick, 2000);
  }, [loadEvent]);

  useEffect(() => () => { if (extendPollRef.current) clearTimeout(extendPollRef.current); }, []);

  const extendRoom = async () => {
    try {
      const { data } = await api.post(`/events/${event.id}/extend/checkout`);
      setCheckoutUrl(data.checkout_url);
      pollExtend(data.session_id); // stay in the room; timer updates once payment lands
    } catch {
      toast.error("Could not start extension payment");
    }
  };

  if (!event) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading reveal…</div>;

  // ---- Left / ended screen ----
  if (left) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-10 max-w-md">
          <PartyPopper className="w-10 h-10 text-gold mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl mb-2">{left === "ended" ? "That's a wrap!" : "You've left the room"}</h1>
          <p className="text-foreground/60 mb-8">{left === "ended" ? "The 15-minute reveal session has ended." : "Thanks for celebrating together."}</p>
          {call?.is_owner && (
            <div className="flex gap-3 justify-center">
              <Button data-testid="goto-dashboard-btn" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 text-black font-semibold">Host dashboard</Button>
              <Button data-testid="goto-home-btn" variant="outline" onClick={() => navigate("/")} className="rounded-full h-12 px-6 border-white/20 bg-transparent">Home</Button>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // ---- Join gate ----
  if (!joined) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-gold/20 blur-[120px]" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-10 w-full max-w-md text-center relative z-10">
          <Sparkles className="w-8 h-8 text-gold mx-auto mb-6" />
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">You're invited to</p>
          <h1 className="font-display font-black text-3xl tracking-tight mb-2">{event.title}</h1>

          {status === "before" && (
            <div data-testid="room-locked" className="mt-6">
              <div className="flex items-center justify-center gap-2 text-foreground/70 mb-3"><Lock className="w-4 h-4" /> Room opens in</div>
              <p className="font-impact text-6xl text-gold" data-testid="opens-countdown">{fmt(opensAt - effNow)}</p>
              <p className="mt-4 text-sm text-foreground/50 flex items-center justify-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {new Date(event.opens_at).toLocaleString()}</p>
            </div>
          )}

          {status === "ended" && (
            <div data-testid="room-ended" className="mt-6">
              <p className="text-foreground/70">This reveal session has ended.</p>
              <Button onClick={() => navigate("/")} className="mt-6 rounded-full h-12 px-6 bg-gold text-black font-semibold">Back home</Button>
            </div>
          )}

          {roomFull && status === "open" && (
            <div data-testid="room-full" className="mt-6">
              <div className="flex items-center justify-center gap-2 text-foreground/70 mb-2"><Lock className="w-4 h-4" /> Room is full</div>
              <p className="text-sm text-foreground/50">The host set a guest limit of {event.max_guests} and every seat is taken right now. Try again shortly in case a seat frees up.</p>
              <Button onClick={() => setRoomFull(false)} className="mt-6 rounded-full h-12 px-6 bg-gold text-black font-semibold">Try again</Button>
            </div>
          )}

          {status === "open" && !roomFull && (
            <>
              <p className="text-foreground/60 mb-6">Hosted by {event.host_name || "the revealer"}. Enter your name and join the live call.</p>
              {inApp && (
                <div data-testid="inapp-browser-warning" className="mb-6 text-left rounded-2xl border border-gold/40 bg-gold/10 p-4">
                  <div className="flex items-center gap-2 text-gold font-semibold mb-1"><ExternalLink className="w-4 h-4" /> Open in your browser</div>
                  <p className="text-sm text-foreground/70 leading-relaxed mb-3">You're in an in-app browser (like Messenger or Instagram), which blocks the camera &amp; mic. Tap the button below to open in {platform === "ios" ? "Safari" : "your browser"}{platform === "ios" ? ", or use the ••• menu and choose \"Open in Safari\"." : "."}</p>
                  <Button type="button" onClick={openInSystemBrowser} data-testid="open-in-browser-btn" className="w-full h-11 rounded-xl bg-gold hover:bg-gold/90 text-black font-semibold mb-2">
                    <ExternalLink className="w-4 h-4 mr-2" /> Open in browser
                  </Button>
                  <Button type="button" onClick={copyLink} data-testid="copy-link-btn" variant="outline" className="w-full h-11 rounded-xl border-gold/40 text-gold hover:bg-gold/10">
                    <Copy className="w-4 h-4 mr-2" /> Copy link instead
                  </Button>
                </div>
              )}
              <form onSubmit={join} className="space-y-4 text-left">
                <Input data-testid="guest-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-14 bg-input border-white/10 rounded-xl text-center" required />
                <label className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                  <Checkbox data-testid="consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                  <span className="text-sm text-foreground/70 leading-relaxed">I allow camera &amp; microphone access and consent to being recorded, and to my reaction possibly appearing in the highlight reel.</span>
                </label>
                <Button data-testid="join-room-btn" type="submit" disabled={!name.trim() || !consent} className="w-full h-14 rounded-xl bg-gold hover:bg-gold/90 text-black font-semibold text-base disabled:opacity-50">
                  <VideoIcon className="w-5 h-5 mr-2" /> Join the reveal
                </Button>
              </form>
              {closesAt && <p className="mt-4 text-xs text-foreground/40">Session lasts 15 minutes from the scheduled start.</p>}
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // ---- Live room ----
  const remaining = closesAt ? closesAt - effNow : null;
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {showAnim && <RevealAnimation event={event} onDone={() => setShowAnim(false)} />}
      <CheckoutOverlay url={checkoutUrl} onClose={() => setCheckoutUrl(null)} />

      {recSecs > 0 && (
        <div className="fixed top-4 right-4 z-[120] flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-white shadow-lg" data-testid="recording-indicator">
          <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" /> Recording reaction · {recSecs}s
        </div>
      )}

      <header className="glass px-4 sm:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 font-display font-bold min-w-0 truncate"><Sparkles className="w-4 h-4 text-gold shrink-0" /> <span className="truncate">{event.title}</span></div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {remaining != null && (
            <div className={`flex items-center gap-1.5 text-sm font-semibold ${remaining < 60000 ? "text-destructive" : "text-foreground/80"}`} data-testid="session-timer">
              <Clock className="w-4 h-4" /> {fmt(remaining)}
            </div>
          )}
          <Button data-testid="mute-toggle-btn" onClick={toggleMute} variant="outline" size="sm" title={muted ? "Unmute reveal sound" : "Mute reveal sound"} className="rounded-full border-white/20 bg-transparent hover:bg-white/10 px-2.5">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-gold" />}
          </Button>
          {call?.is_owner && (
            <Button data-testid="extend-room-btn" onClick={extendRoom} variant="outline" size="sm" className="rounded-full border-gold/40 text-gold bg-transparent hover:bg-gold/10">
              <Clock className="w-4 h-4 mr-1.5" /> +5 min (${(event.max_guests * 0.25).toFixed(2)})
            </Button>
          )}
          <Button data-testid="leave-room-btn" onClick={leaveRoom} variant="outline" size="sm" className="rounded-full border-white/20 bg-transparent hover:bg-destructive/20">
            <LogOut className="w-4 h-4 mr-1.5" /> Leave
          </Button>
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-[1fr_360px] gap-4 p-4 min-h-0">
        <div className="relative rounded-2xl overflow-hidden min-h-[50vh] lg:min-h-0">
          {call && <VideoCall roomUrl={call.room_url} token={call.token} onParticipantsChange={handleParticipants} onCall={handleCall} />}
          {soundHint && (
            <button
              type="button"
              data-testid="enable-sound-hint"
              onClick={() => {
                document.querySelectorAll("audio").forEach((a) => { a.muted = false; a.play().catch(() => {}); });
                setSoundHint(false);
                toast.success("Sound on");
              }}
              className="absolute inset-x-0 bottom-0 z-[60] m-3 flex items-center justify-center gap-2 rounded-xl bg-gold text-black font-semibold px-4 py-3 shadow-lg animate-pulse"
            >
              <Volume2 className="w-5 h-5" /> Tap to turn on sound
            </button>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="glass rounded-2xl p-5" data-testid="presence-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.25em] text-foreground/60 font-bold">Who's here</p>
              <span className="text-sm font-semibold text-forest flex items-center gap-1.5" data-testid="presence-count">
                <span className="w-2 h-2 rounded-full bg-forest animate-pulse" />{participants.length}
              </span>
            </div>
            {participants.length === 0 ? (
              <p className="text-sm text-foreground/40">Connecting…</p>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto" data-testid="presence-list">
                {participants.map((p) => (
                  <span key={p.id} className={`text-xs px-3 py-1.5 rounded-full border ${p.local ? "border-gold/60 bg-gold/10 text-white" : "border-white/10 bg-white/5 text-foreground/80"}`}>
                    {p.name}{p.local ? " (you)" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-6 flex-1 flex flex-col items-center justify-center text-center">
            {!event.revealed && !showAnim && !revealedRef.current ? (
              call?.is_owner ? (
                <>
                  <p className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-6">Host controls</p>
                  <motion.button data-testid="press-reveal-button" onClick={doReveal} disabled={triggering} whileTap={{ scale: 0.92 }}
                    className="w-52 h-52 rounded-full bg-gold text-black flex flex-col items-center justify-center animate-pulse-glow disabled:opacity-60">
                    {triggering ? <Loader2 className="w-10 h-10 animate-spin" /> : (
                      <>
                        <span className="font-impact text-4xl leading-none">REVEAL</span>
                        <span className="text-xs mt-1 opacity-80">press when ready</span>
                      </>
                    )}
                  </motion.button>
                  <p className="mt-6 text-sm text-foreground/50">
                    {participants.length > 1 ? `${participants.length} people are here — press when everyone's ready!` : "Wait for guests to join, then press to reveal."}
                  </p>
                </>
              ) : (
                <>
                  <Circle className="w-10 h-10 text-gold mb-4 animate-pulse" />
                  <p className="font-display font-bold text-xl">Waiting for the reveal…</p>
                  <p className="mt-2 text-sm text-foreground/50">The host will press the button any moment now. When it happens we'll record your reaction for 20 seconds — get ready!</p>
                </>
              )
            ) : (
              <div className="w-full">
                <PartyPopper className="w-10 h-10 text-gold mx-auto mb-3" />
                <p className="font-display font-black text-2xl mb-1">It's out!</p>
                <p className="text-sm text-foreground/60">{recSecs > 0 ? "Capturing your reaction…" : "Thanks for celebrating together."}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
